import React, { useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { propTypes } from '../../../util/types';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { Button, FieldTextInput, Form, OutsideClickHandler } from '../../../components';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from '../../../util/urlHelpers';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routeConfiguration';
import camelCase from 'lodash/camelCase';
import trim from 'lodash/trim';

import css from './SearchPanel.css';
import { TRANSITIONS } from '../../../util/customTransaction/mainRentalTransaction';
import { EXTEND_TRANSITIONS } from '../../../util/customTransaction/extendRentalTransaction';

const getSearchType = ({ customerId, providerId, listingId, transactionId }) => {
  if (transactionId) {
    return 'transaction id: ';
  }

  if (customerId) {
    return 'customer id: ';
  }

  if (providerId) {
    return 'provider id: ';
  }

  if (listingId) {
    return 'listing id: ';
  }

  return '';
};

const SearchPanel = ({ transactions }) => {
  const intl = useIntl();
  const [focusedForm, setFocusedForm] = useState(false);
  const [keyword, setKeyword] = useState();
  const transitionNames = [...TRANSITIONS, ...EXTEND_TRANSITIONS];

  const location = useLocation();
  const history = useHistory();

  const { last_transition, customerId, providerId, listingId, transactionId, ...rest } = parse(
    location.search
  );

  useEffect(() => {
    const type = getSearchType({
      last_transition,
      customerId,
      providerId,
      listingId,
      transactionId,
    });
    if (type && (last_transition || customerId || providerId || listingId || transactionId))
      setKeyword(
        `${type}${last_transition || customerId || providerId || listingId || transactionId}`
      );
  }, [last_transition, customerId, providerId, listingId, transactionId]);

  const onSubmit = values => {
    handleEnterId(values.keyword);
  };

  const handleFocus = () => {
    setFocusedForm(true);
  };

  const handleBlur = () => {
    setFocusedForm(false);
  };

  const handleClear = () => {
    setKeyword('');
    const routes = routeConfiguration();
    const searchParams = {
      page: rest.page || 1,
      ...(rest.per_page ? { per_page: rest.per_page } : {}),
    };
    history.push(
      createResourceLocatorString('AdminManageTransactionsPage', routes, {}, searchParams)
    );
  };

  const handleSelectTransition = name => () => {
    const routes = routeConfiguration();
    handleBlur();
    const searchParams = {
      page: rest.page,
      per_page: rest.per_page,
      last_transition: name,
    };
    history.push(
      createResourceLocatorString('AdminManageTransactionsPage', routes, {}, searchParams)
    );
  };

  const handleEnterId = keyword => {
    if (keyword.includes('transition/')) {
      handleSelectTransition(keyword)();
      return;
    }
    handleBlur();
    const splittedKeyword = keyword.split(':');
    const type = camelCase(splittedKeyword[0]);
    const id = trim(splittedKeyword[1]);
    const routes = routeConfiguration();
    const searchParams = {
      page: rest.page,
      perPage: rest.per_page,
      [type]: id,
    };
    history.push(
      createResourceLocatorString('AdminManageTransactionsPage', routes, {}, searchParams)
    );
  };

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={{
        keyword,
      }}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const { handleSubmit, values, form, active } = formRenderProps;

        const handleSelectType = type => () => {
          setKeyword(type);
          handleBlur();
          form.focus('keyword');
        };

        const keywordPlaceholder = intl.formatMessage({
          id: 'AMTxPage.searchPanel.keywordPlaceholder',
          defaultMessage: 'Filter by last transition, e.g. "transition/accept"',
        });

        return (
          <Form onSubmit={handleSubmit} className={css.searchPanel}>
            <div className={css.fieldsContainer}>
              <Button rootClassName={css.submitBtn} type="submit">
                <svg viewBox="0 0 18 18" height="18" width="18">
                  <path
                    stroke-linejoin="round"
                    stroke-linecap="round"
                    fill-rule="evenodd"
                    fill="none"
                    stroke-width="1.5"
                    stroke="currentColor"
                    d="M11 13V9l5.586-5.585a1.415 1.415 0 00-1-2.415H2.414a1.415 1.415 0 00-1 2.415L7 9v8l4-4z"
                  ></path>
                </svg>
              </Button>
              <OutsideClickHandler onOutsideClick={handleBlur} className={css.fieldWrapper}>
                <FieldTextInput
                  name="keyword"
                  className={css.keyword}
                  placeholder={keywordPlaceholder}
                  autoComplete="off"
                  onFocus={handleFocus}
                />
                {focusedForm && (
                  <div className={css.suggestion}>
                    <div className={css.suggestionTitle}>
                      <FormattedMessage
                        id="AMTxPage.searchPanel.suggestedTransactions"
                        defaultMessage="Suggested transitions:"
                      />
                    </div>
                    <div>
                      <div
                        onClick={handleSelectType('transaction id: ')}
                        className={css.transitionName}
                      >
                        <FormattedMessage
                          id="AMTxPage.searchPanel.suggestion.transactionId"
                          defaultMessage="transaction id: "
                        />
                      </div>
                      <div
                        onClick={handleSelectType('customer id: ')}
                        className={css.transitionName}
                      >
                        <FormattedMessage
                          id="AMTxPage.searchPanel.suggestion.customerId"
                          defaultMessage="customer id: "
                        />
                      </div>
                      <div
                        onClick={handleSelectType('provider id: ')}
                        className={css.transitionName}
                      >
                        <FormattedMessage
                          id="AMTxPage.searchPanel.suggestion.providerId"
                          defaultMessage="provider id: "
                        />
                      </div>
                      <div
                        onClick={handleSelectType('listing id: ')}
                        className={css.transitionName}
                      >
                        <FormattedMessage
                          id="AMTxPage.searchPanel.suggestion.listingId"
                          defaultMessage="listing id: "
                        />
                      </div>
                      {transitionNames.map(name => {
                        return (
                          <div
                            key={name}
                            onClick={handleSelectTransition(name)}
                            className={css.transitionName}
                          >
                            {name}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </OutsideClickHandler>
              <Button rootClassName={css.cancelBtn} onClick={handleClear}>
                <svg viewBox="0 0 12 12" height="12" width="12">
                  <g fill-rule="evenodd" transform="translate(-1 -1)">
                    <rect
                      rx="1"
                      height="2"
                      width="16"
                      y="6"
                      x="-1"
                      transform="rotate(45 7 7)"
                    ></rect>
                    <rect
                      rx="1"
                      height="2"
                      width="16"
                      y="6"
                      x="-1"
                      transform="rotate(-45 7 7)"
                    ></rect>
                  </g>
                </svg>
              </Button>
            </div>
          </Form>
        );
      }}
    />
  );
};

SearchPanel.propTypes = {
  transaction: propTypes.transaction,
};

export default SearchPanel;
